import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { trackClusterConnected } from '@cast/infrastructure-analytics';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  useDoesReportingMonetizationApply,
  useTrial,
} from '@cast/shared-trial-ui';
import { K8sProvider } from '@cast/shared-types';
import { Cluster } from '@cast/types';

import { isKentEligibilityDataAvailable } from 'features/kent/utils';
import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';
import {
  AutoscalerType,
  FeatureName,
  FeatureSelection,
} from 'hooks/dialogs/cluster/types';

import { useClusterConnectionTimeout } from './_hooks/useClusterConnectionTimeout';
import { PROVIDERS_WITHOUT_FEATURE_SELECTION } from './constants';
import { OnboardClusterContextValue, OnboardingState } from './types';
import {
  useFeatureSelection,
  buildValuesForFeaturePromotion,
  COST_MONITORING_ONLY,
  getDefaultFeatureSelection,
  getKentFeatureSelection,
} from '../_hooks/useFeatureSelection';
import { useGetNewlyOnboardedClusters } from '../_hooks/useGetNewlyOnboardedClusters';
import { useWerePhase2FeaturesEnabled } from '../_hooks/useWerePhase2FeaturesEnabled';

const DEFAULT_PROVIDER = K8sProvider.EKS;

export const OnboardClusterContext = createContext<OnboardClusterContextValue>(
  {} as never
);

type Props = {
  initialProvider: K8sProvider;
  initialAnywhereClusterName?: string;
  promotedFeature?: FeatureName;
  onClusterConnected: (cluster: Cluster) => void;
  onFeaturesSelected: (features: FeatureSelection) => void;
  onCompleted: () => void;
};

export const OnboardClusterProvider = ({
  children,
  initialProvider,
  initialAnywhereClusterName,
  promotedFeature,
  onClusterConnected,
  onFeaturesSelected,
  onCompleted,
}: PropsWithChildren<Props>) => {
  const { currentOrganization } = useOrganizations();
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const { isExpired: isTrialExpired } = useTrial();
  const reportingMonetizationApplies = useDoesReportingMonetizationApply();
  const [provider, setProvider] = useState<K8sProvider>(
    initialProvider || DEFAULT_PROVIDER
  );
  const [autoscalerType, setAutoscalerType] =
    useState<AutoscalerType>('cast-ai');

  const [connectedCluster, setConnectedCluster] = useState<
    Cluster | undefined
  >();

  const { featureSelection, updateFeature, resetFeatures } =
    useFeatureSelection({
      initialValues: promotedFeature
        ? buildValuesForFeaturePromotion(promotedFeature, {
            isSecuritySunsetEnabled,
            isTrialExpired,
          })
        : getDefaultFeatureSelection({
            isSecuritySunsetEnabled,
            isTrialExpired,
            reportingMonetizationApplies,
          }),
    });

  const [state, setState] = useState<OnboardingState>('selectProvider');

  const getFeatureDefaults = useCallback(
    (provider: K8sProvider, autoscalerType: AutoscalerType) => {
      if (provider === K8sProvider.ROSA || provider === K8sProvider.ANYWHERE) {
        return COST_MONITORING_ONLY;
      }
      if (provider === K8sProvider.EKS && autoscalerType === 'karpenter') {
        return getKentFeatureSelection();
      }
      return promotedFeature
        ? buildValuesForFeaturePromotion(promotedFeature, {
            isSecuritySunsetEnabled,
            isTrialExpired,
          })
        : getDefaultFeatureSelection({
            isSecuritySunsetEnabled,
            isTrialExpired,
            reportingMonetizationApplies,
          });
    },
    [
      promotedFeature,
      isSecuritySunsetEnabled,
      isTrialExpired,
      reportingMonetizationApplies,
    ]
  );

  const handleSetAutoscalerType = useCallback(
    (newAutoscalerType: AutoscalerType) => {
      setAutoscalerType(newAutoscalerType);
      updateFeature('kent', newAutoscalerType === 'karpenter');
    },
    [updateFeature]
  );

  const handleSetProvider = useCallback(
    (newProvider: K8sProvider) => {
      setProvider(newProvider);
      const newAutoscalerType =
        newProvider !== K8sProvider.EKS ? 'cast-ai' : autoscalerType;
      if (newProvider !== K8sProvider.EKS) {
        setAutoscalerType('cast-ai');
      }
      resetFeatures(getFeatureDefaults(newProvider, newAutoscalerType));
    },
    [autoscalerType, resetFeatures, getFeatureDefaults]
  );

  const selectFeatures = useCallback(() => {
    setState('selectFeatures');
  }, []);

  const goBackFromConnecting = useCallback(() => {
    if (PROVIDERS_WITHOUT_FEATURE_SELECTION.includes(provider)) {
      setState('selectProvider');
    } else {
      setState('selectFeatures');
    }
  }, [provider]);

  const handleResetState = useCallback(() => {
    setConnectedCluster(undefined);
    setState('selectProvider');
  }, []);

  const isKentOnboarding = autoscalerType === 'karpenter';

  useClusterConnectionTimeout(state, setState, 60 * 7 * 1000);

  const { newConnections, refetchClusters } = useGetNewlyOnboardedClusters({
    provider,
    polling: state === 'connecting' && !connectedCluster,
  });

  const featureEnablementStatus = useWerePhase2FeaturesEnabled({
    cluster: connectedCluster ?? ({} as Cluster),
    featureSelection,
    enabled: !!connectedCluster && state === 'connecting',
    onSuccess: () => {
      setState('success');
    },
    onError: () => setState('error'),
  });

  const handleWaitForConnection = useCallback(() => {
    setState('connecting');
    onFeaturesSelected(featureSelection);
    refetchClusters();
  }, [featureSelection, onFeaturesSelected, refetchClusters]);

  useEffect(() => {
    if (newConnections.length > 0 && state === 'connecting') {
      const cluster = newConnections[0];

      if (isKentOnboarding && !isKentEligibilityDataAvailable(cluster)) {
        return;
      }

      if (currentOrganization?.id) {
        trackClusterConnected({
          organization_id: currentOrganization.id,
          cluster_id: cluster.id,
        });
      }
      onClusterConnected(cluster);

      if (isKentOnboarding) {
        // User feedback is handled by a background job, so we don't need to show anything here.
        onCompleted();
        return;
      }

      setConnectedCluster(cluster);

      if (provider === K8sProvider.ANYWHERE) {
        setState('configureCastAnywhere');
      }
    }
  }, [
    provider,
    newConnections,
    state,
    isKentOnboarding,
    onCompleted,
    onClusterConnected,
    currentOrganization?.id,
  ]);

  return (
    <OnboardClusterContext.Provider
      value={{
        state,
        goBackFromConnecting,
        selectFeatures,
        handleWaitForConnection,
        handleResetState,
        provider,
        setProvider: handleSetProvider,
        autoscalerType,
        setAutoscalerType: handleSetAutoscalerType,
        newCluster: connectedCluster,
        isClusterConnected: !!connectedCluster,
        featureSelection,
        updateFeature,
        initialAnywhereClusterName,
        featureEnablementStatus,
      }}
    >
      {children}
    </OnboardClusterContext.Provider>
  );
};
